/**
 * @fileoverview Guacamole Console Composable (Credential-Free)
 *
 * Manages Guacamole WebSocket connection for VM console access.
 * Uses official Apache Guacamole guacamole-common-js 1.6.0 library
 * accessed via window.Guacamole global (loaded via script tag in index.html).
 *
 * SECURITY MODEL:
 * - Frontend NEVER receives VM credentials (username, password)
 * - Backend stores credentials encrypted (Fernet) in PostgreSQL database
 * - Backend decrypts credentials on-demand when issuing console token
 * - Console token (JWT) contains encrypted connection parameters
 * - Token embedded in WebSocket URL: wss://host/ws?token={ws_token}
 * - Backend acts as transparent proxy, intercepting 'connect' instruction only
 * - Credentials exist only in memory during active console session
 *
 * ARCHITECTURE (Multi-Region WebSocket):
 * 1. Frontend calls POST /v1/console/{vmId}/token (authenticated users)
 *    OR POST /v1/shares/{shareId}/vms/{vmId}/console/token (share users)
 * 2. Backend retrieves encrypted credentials from lab_vm table
 * 3. Backend decrypts credentials using FERNET_KEY
 * 4. Backend generates JWT with decrypted credentials + metadata
 * 5. Backend returns { ws_token, ws_url, expires_in }
 * 6. Frontend connects to regional WebSocket URL with token
 * 7. Backend validates JWT, connects to guacd, sends 'select,{protocol}'
 * 8. guacd responds with 'args' → backend forwards to frontend
 * 9. Frontend (guacamole-common-js) sends 'connect' with placeholder values
 * 10. Backend intercepts 'connect', injects real credentials, forwards to guacd
 * 11. guacd validates credentials and sends 'ready' → backend forwards to frontend
 * 12. Frontend transitions to CONNECTED state, sync handler registered
 */

import { ref, onUnmounted } from 'vue'
import { getConsoleToken, getShareConsoleToken } from '@/api/console'

/**
 * @typedef {'idle'|'connecting'|'connected'|'disconnected'|'reconnecting'|'error'} ConnectionState
 */

/**
 * Debug logger - only logs in development mode
 * @param {string} message - Log message
 * @param {any} [data] - Optional data to log
 */
const debug = (message, data) => {
  if (import.meta.env.DEV) {
    if (data !== undefined) {
      console.debug(message, data)
    } else {
      console.debug(message)
    }
  }
}

/**
 * Guacamole key symbols for special key combinations
 * @see https://guacamole.apache.org/doc/gug/protocol-reference.html#key-event-instruction
 */
const KEY_SYMBOLS = {
  CTRL: 0xffe3, // 65507
  ALT: 0xffe9, // 65513
  DELETE: 0xffff // 65535
}

/**
 * Guacamole console composable
 *
 * Manages WebSocket-based console connection using guacamole-common-js.
 * Handles client initialization, connection lifecycle, input setup, and auto-reconnect.
 *
 * IMPORTANT: Requires window.Guacamole to be loaded via script tag in index.html.
 * Uses official Apache Guacamole library (no npm package).
 *
 * @param {Object} [options] - Configuration options
 * @param {number} [options.maxReconnectAttempts=5] - Maximum reconnection attempts before giving up
 * @param {number} [options.initialReconnectDelay=1000] - Initial delay in ms before first reconnect
 * @param {number} [options.maxReconnectDelay=30000] - Maximum delay in ms between reconnect attempts
 * @param {Function} [options.onClipboardReceived] - Callback when clipboard data received from remote
 * @returns {Object} Console connection state and controls
 * @example
 * import { useConsole } from '@/composables/useConsole'
 * import { getVMConsole } from '@/api/labs'
 *
 * const {
 *   initializeClient,
 *   connect,
 *   disconnect,
 *   connectionState,
 *   errorMessage,
 *   reconnect,
 *   reconnectAttempts,
 *   sendCtrlAltDel
 * } = useConsole({
 *   maxReconnectAttempts: 5,
 *   onClipboardReceived: (text) => console.debug('Clipboard:', text)
 * })
 *
 * onMounted(async () => {
 *   const displayEl = displayRef.value
 *   initializeClient(displayEl)
 *
 *   const { websocket_url, connection_id } = await getVMConsole(labId, vmId)
 *   connect(websocket_url)
 * })
 */
export function useConsole(options = {}) {
  const {
    maxReconnectAttempts = 5,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
    onClipboardReceived = null
  } = options
  /** @type {import('vue').Ref<ConnectionState>} */
  const connectionState = ref('idle')

  /** @type {import('vue').Ref<string|null>} */
  const errorMessage = ref(null)

  /** @type {import('vue').Ref<number>} Current reconnection attempt count */
  const reconnectAttempts = ref(0)

  /** @type {import('vue').Ref<number>} Current reconnection delay in ms */
  const reconnectDelay = ref(initialReconnectDelay)

  /** @type {Object|null} Guacamole.Client instance */
  let client = null

  /** @type {Object|null} Guacamole.WebSocketTunnel instance */
  let tunnel = null

  /** @type {Object|null} Guacamole.Keyboard instance */
  let keyboard = null

  /** @type {boolean} Whether keyboard input is enabled */
  let keyboardEnabled = true

  /** @type {Object|null} Guacamole.Mouse instance */
  let mouse = null

  /** @type {number|null} Reconnection timeout ID */
  let reconnectTimeoutId = null

  /** @type {Object|null} Stored connection parameters for reconnection */
  let connectionParams = null

  /** @type {ResizeObserver|null} Observer for container resize events */
  let resizeObserver = null

  /**
   * Initialize Guacamole client and attach to display element
   *
   * Stores the display element reference for later use when creating
   * the Guacamole client during connection. The client must be created
   * WITH a valid tunnel, so we defer client creation until connect().
   *
   * @param {HTMLElement} displayElement - DOM element to attach console display
   * @throws {Error} If window.Guacamole is not available
   * @example
   * const displayRef = ref(null)
   *
   * onMounted(() => {
   *   initializeClient(displayRef.value)
   * })
   *
   * <template>
   *   <div ref="displayRef" class="guacamole-display"></div>
   * </template>
   */
  function initializeClient(displayElement) {
    debug('[useConsole] Initializing client...')
    debug('[useConsole] window.Guacamole available:', !!window.Guacamole)

    if (!window.Guacamole) {
      console.error('[useConsole] Guacamole library not found on window object')
      throw new Error(
        'Guacamole library not loaded. Ensure guacamole-common-js is included in index.html'
      )
    }

    // Log available Guacamole API
    debug('[useConsole] Guacamole API:', {
      hasClient: !!window.Guacamole.Client,
      hasWebSocketTunnel: !!window.Guacamole.WebSocketTunnel,
      hasKeyboard: !!window.Guacamole.Keyboard,
      hasMouse: !!window.Guacamole.Mouse,
      clientStates: window.Guacamole.Client?.State
    })

    if (!displayElement) {
      console.error('[useConsole] Display element is required but not provided')
      throw new Error('Display element is required')
    }

    debug('[useConsole] Display element:', displayElement)

    // Store display element for use during connect()
    // We cannot create the client here because Guacamole.Client
    // requires a valid tunnel at construction time
    connectionParams = { displayElement }

    connectionState.value = 'idle'
    errorMessage.value = null
    debug('[useConsole] Client initialized successfully')
  }

  /**
   * Connect to authenticated VM console via WebSocket
   *
   * Two-step connection process:
   * 1. Get WebSocket token from central API
   * 2. Connect to regional WebSocket with token
   *
   * Creates the Guacamole client with a WebSocket tunnel and establishes
   * the connection. Sets up keyboard and mouse input handlers.
   * Stores connection parameters for auto-reconnect.
   *
   * @param {string} vmId - VM ID (prefixed format: vm-xxx)
   * @throws {Error} If initializeClient not called first or token fetch fails
   * @example
   * // For authenticated users (Okta)
   * await connect(vmId)
   */
  async function connect(vmId) {
    if (!connectionParams || !connectionParams.displayElement) {
      throw new Error('Client not initialized. Call initializeClient() first.')
    }

    try {
      // Store vmId and connection type for reconnection
      connectionParams.vmId = vmId
      connectionParams.connectionType = 'authenticated'

      debug('[useConsole] Starting connection for VM:', vmId)
      connectionState.value = 'connecting'
      errorMessage.value = null

      // Step 1: Get regional WebSocket token from central API
      debug('[useConsole] Step 1: Fetching WebSocket token from central API...')
      const { ws_token, ws_url } = await getConsoleToken(vmId)
      debug('[useConsole] Token received, regional WebSocket URL:', ws_url)

      // Step 2: Build WebSocket URL with token
      const websocketUrl = `${ws_url}?token=${encodeURIComponent(ws_token)}`
      debug(
        '[useConsole] WebSocket URL:',
        websocketUrl.replace(/token=[^&]+/, 'token=***MASKED***')
      )

      // Step 3: Verify display element has valid dimensions before connecting
      const displayElement = connectionParams.displayElement
      const rect = displayElement.getBoundingClientRect()
      debug('[useConsole] Display element dimensions:', {
        width: rect.width,
        height: rect.height,
        offsetWidth: displayElement.offsetWidth,
        offsetHeight: displayElement.offsetHeight
      })

      if (rect.width === 0 || rect.height === 0) {
        const error = new Error(
          'Display element has zero dimensions. Ensure the container is visible and sized before connecting.'
        )
        console.error('[useConsole] Invalid display dimensions:', error)
        throw error
      }

      // Step 4: Create WebSocket tunnel and connect
      // ARCHITECTURE: Backend acts as transparent proxy to guacd
      // - Backend initiates handshake by sending 'select,{protocol}' to guacd
      // - guacd responds with 'args' instruction, backend forwards to frontend
      // - Frontend sends 'size', 'audio', 'video', 'image', 'connect' instructions
      // - Backend intercepts 'connect' instruction and injects real credentials
      // - guacd sends 'ready' instruction, backend forwards to frontend
      // - Frontend transitions to CONNECTED and registers sync handler
      debug('[useConsole] Creating WebSocketTunnel...')
      tunnel = new window.Guacamole.WebSocketTunnel(websocketUrl)

      // CRITICAL: Intercept sendMessage to log and validate all outgoing instructions
      const originalSendMessage = tunnel.sendMessage.bind(tunnel)
      tunnel.sendMessage = function (...args) {
        const opcode = args[0]
        debug('[useConsole] Tunnel sendMessage intercepted:', { opcode, args })

        // If this is a size instruction, validate and fix dimensions
        // IMPORTANT: guacd only expects size,width,height (NO DPI)
        if (opcode === 'size') {
          const MIN_DIMENSION = 100
          let width = parseInt(args[1], 10) || 1024
          let height = parseInt(args[2], 10) || 768

          // Enforce minimum dimensions
          if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
            console.warn('[useConsole] Blocking invalid size instruction:', { width, height })
            width = Math.max(MIN_DIMENSION, width) || 1024
            height = Math.max(MIN_DIMENSION, height) || 768
            debug('[useConsole] Sending corrected size:', { width, height })
          }

          // Send corrected size (NO DPI - guacd doesn't expect it)
          return originalSendMessage('size', width.toString(), height.toString())
        }

        // For all other instructions, pass through unchanged
        return originalSendMessage.apply(this, args)
      }

      // Set tunnel state handler for debugging
      tunnel.onstatechange = state => {
        const stateNames = {
          0: 'CONNECTING',
          1: 'OPEN',
          2: 'CLOSED'
        }
        debug('[useConsole] Tunnel state changed:', stateNames[state] || state)
      }

      // Set tunnel error handler before creating client
      tunnel.onerror = status => {
        console.error('[useConsole] Tunnel error:', status)
        handleError({
          message: `Tunnel error: ${status.message || 'Connection failed'}`,
          code: status.code
        })
      }

      // Create Guacamole client WITH the tunnel
      // CRITICAL: Client must be created with a valid tunnel, not null
      // NOTE: The SERVER handles the full handshake - frontend just receives display data
      debug('[useConsole] Creating Guacamole client with tunnel...')
      client = new window.Guacamole.Client(tunnel)

      // Attach display to DOM element
      displayElement.appendChild(client.getDisplay().getElement())

      // Set up state change handler
      client.onstatechange = handleStateChange

      // Set up error handler
      client.onerror = handleError

      // Set up sync handler EARLY - BEFORE client.connect()
      // CRITICAL: Handler must be registered before the client receives 'ready' instruction
      // In the new architecture, the backend forwards all instructions from guacd,
      // and the frontend must have handlers ready to process them
      // guacamole-common-js handles sync protocol automatically via this handler
      client.onsync = timestamp => {
        debug('[useConsole] Sync received from guacd, timestamp:', timestamp)
        scaleDisplayToFit()
      }
      debug('[useConsole] Sync handler registered')

      // Set up clipboard receive handler
      if (onClipboardReceived) {
        client.onclipboard = stream => {
          const reader = new window.Guacamole.StringReader(stream)
          let clipboardText = ''

          reader.ontext = text => {
            clipboardText += text
          }

          reader.onend = () => {
            onClipboardReceived(clipboardText)
          }
        }
      }

      // Connect the client (tunnel is already set in constructor)
      // ARCHITECTURE NOTE (FastAPI WebSocket):
      // - client.connect(data) opens the WebSocket tunnel
      // - Client sends 'select' instruction (via onstatechange handler above)
      // - Backend forwards 'select' to guacd (replacing protocol with server-side value)
      // - guacd responds with 'args', backend forwards to client
      // - Client sends 'size', 'audio', 'video', 'image', 'connect' instructions
      // - Backend intercepts 'connect' and injects real credentials (from database)
      // - guacd validates credentials and sends 'ready' → client CONNECTED
      debug('[useConsole] Calling client.connect() (credentials handled by backend)...')
      client.connect('')
      debug('[useConsole] Client connect() called, WebSocket tunnel opening...')

      // Set up keyboard input
      // No state check needed - guacamole-common-js handles protocol
      keyboard = new window.Guacamole.Keyboard(document)

      // Track Ctrl/Cmd key state for detecting Ctrl+V / Cmd+V paste shortcut
      let modifierPressed = false
      const KEYSYM_CTRL_L = 0xffe3 // 65507 - Left Ctrl
      const KEYSYM_CTRL_R = 0xffe4 // 65508 - Right Ctrl
      const KEYSYM_META_L = 0xffe7 // 65511 - Left Meta (Cmd on Mac)
      const KEYSYM_META_R = 0xffe8 // 65512 - Right Meta (Cmd on Mac)
      const KEYSYM_V_LOWER = 0x76 // 118
      const KEYSYM_V_UPPER = 0x56 // 86

      const isModifierKey = keysym =>
        keysym === KEYSYM_CTRL_L ||
        keysym === KEYSYM_CTRL_R ||
        keysym === KEYSYM_META_L ||
        keysym === KEYSYM_META_R

      keyboard.onkeydown = keysym => {
        if (client && keyboardEnabled) {
          // Track Ctrl/Cmd key state
          if (isModifierKey(keysym)) {
            modifierPressed = true
          }

          // If Ctrl+V or Cmd+V is pressed, read clipboard and send to VM
          if (modifierPressed && (keysym === KEYSYM_V_LOWER || keysym === KEYSYM_V_UPPER)) {
            debug('[useConsole] Ctrl+V / Cmd+V detected, reading clipboard...')
            // Read clipboard using Clipboard API and send to VM
            navigator.clipboard
              .readText()
              .then(text => {
                if (text) {
                  debug('[useConsole] Sending clipboard to VM:', text.substring(0, 50) + '...')
                  sendClipboard(text)
                }
              })
              .catch(err => {
                console.warn('[useConsole] Could not read clipboard:', err.message)
              })
            return false // Prevent default - we handle it ourselves
          }

          client.sendKeyEvent(1, keysym)
          return false // Prevent browser default only when we handle it
        }
        return true // Allow browser default when keyboard is disabled
      }
      keyboard.onkeyup = keysym => {
        if (client && keyboardEnabled) {
          // Track Ctrl/Cmd key state
          if (isModifierKey(keysym)) {
            modifierPressed = false
          }

          client.sendKeyEvent(0, keysym)
          return false // Prevent browser default only when we handle it
        }
        return true // Allow browser default when keyboard is disabled
      }

      // Set up mouse input
      // No state check needed - guacamole-common-js handles protocol
      const display = client.getDisplay()
      mouse = new window.Guacamole.Mouse(display.getElement())

      mouse.onmousedown =
        mouse.onmouseup =
        mouse.onmousemove =
          mouseState => {
            if (client) {
              client.sendMouseState(mouseState)
            }
          }

      // Handle touch events for mobile
      const touch = new window.Guacamole.Mouse.Touchpad(display.getElement())
      touch.onmousedown =
        touch.onmouseup =
        touch.onmousemove =
          mouseState => {
            if (client) {
              client.sendMouseState(mouseState)
            }
          }

      // Set up paste handler for clipboard sync (Ctrl+V / Cmd+V → VM)
      const handlePaste = e => {
        if (client && connectionState.value === 'connected') {
          const text = e.clipboardData?.getData('text/plain')
          if (text) {
            debug('[useConsole] Paste detected, sending to VM clipboard')
            sendClipboard(text)
          }
        }
      }

      document.addEventListener('paste', handlePaste)

      // Store reference for cleanup
      connectionParams.pasteHandler = handlePaste
    } catch (err) {
      errorMessage.value = err.message || 'Failed to connect'
      connectionState.value = 'error'
      throw err
    }
  }

  /**
   * Scale the Guacamole display to fit the container
   *
   * Calculates the appropriate scale factor to fit the canvas within
   * the container while maintaining aspect ratio. Only scales down,
   * never scales up beyond 100%.
   *
   * @private
   */
  function scaleDisplayToFit() {
    if (!client || !connectionParams || !connectionParams.displayElement) {
      return
    }

    try {
      const display = client.getDisplay()
      const displayElement = connectionParams.displayElement

      // Get display dimensions (actual canvas size from server)
      const displayWidth = display.getWidth()
      const displayHeight = display.getHeight()

      // Get container dimensions
      const containerRect = displayElement.getBoundingClientRect()
      const containerWidth = containerRect.width
      const containerHeight = containerRect.height

      debug('[useConsole] Scaling display:', {
        displayWidth,
        displayHeight,
        containerWidth,
        containerHeight
      })

      // Calculate scale to fit while maintaining aspect ratio
      const scaleX = containerWidth / displayWidth
      const scaleY = containerHeight / displayHeight
      const scale = Math.min(scaleX, scaleY, 1) // Don't scale up, only down

      debug('[useConsole] Applying scale:', scale)
      display.scale(scale)
    } catch (err) {
      console.warn('[useConsole] Failed to scale display:', err)
    }
  }

  /**
   * Set up ResizeObserver to handle container resizing
   *
   * Monitors the container element for size changes and rescales
   * the display accordingly. Cleans up previous observer if exists.
   *
   * @private
   */
  function setupResizeObserver() {
    if (!connectionParams || !connectionParams.displayElement) {
      return
    }

    // Clean up existing observer
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }

    // Create new observer
    resizeObserver = new ResizeObserver(() => {
      // Recalculate and apply scale when container size changes
      scaleDisplayToFit()
    })

    // Observe the container element
    resizeObserver.observe(connectionParams.displayElement)
    debug('[useConsole] ResizeObserver set up for container')
  }

  /**
   * Disconnect from console and clean up resources
   *
   * Closes WebSocket tunnel, removes input handlers, cleans up display,
   * and resets state. Cancels any pending reconnection attempts.
   * Safe to call multiple times.
   *
   * @param {boolean} [shouldReconnect=false] - If true, attempt auto-reconnect
   * @example
   * disconnect()
   */
  function disconnect(shouldReconnect = false) {
    // Clear any pending reconnect timeout
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }

    // Clean up ResizeObserver
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }

    // Remove keyboard listener
    if (keyboard) {
      // Guacamole.Keyboard needs to be properly disposed
      // by clearing event handlers to prevent memory leaks
      keyboard.onkeydown = null
      keyboard.onkeyup = null
      keyboard = null
    }

    // Remove mouse listeners
    if (mouse) {
      // Clear mouse event handlers to prevent stale references
      mouse.onmousedown = null
      mouse.onmouseup = null
      mouse.onmousemove = null
      mouse = null
    }

    // Remove paste listener
    if (connectionParams?.pasteHandler) {
      document.removeEventListener('paste', connectionParams.pasteHandler)
      connectionParams.pasteHandler = null
    }

    // Disconnect client and clean up display
    if (client) {
      // Remove display element from DOM before disconnecting client
      const displayElement = connectionParams?.displayElement
      if (displayElement && client.getDisplay) {
        try {
          const display = client.getDisplay()
          if (display) {
            const guacDisplayElement = display.getElement()
            if (guacDisplayElement && displayElement.contains(guacDisplayElement)) {
              displayElement.removeChild(guacDisplayElement)
            }
          }
        } catch (err) {
          console.warn('Error removing display element:', err)
        }
      }

      // Disconnect client
      try {
        client.disconnect()
      } catch (err) {
        console.warn('Error disconnecting client:', err)
      }

      client = null
    }

    // Close tunnel
    if (tunnel) {
      tunnel.disconnect()
      tunnel = null
    }

    connectionState.value = 'disconnected'

    // Attempt auto-reconnect if requested and we have connection params
    if (shouldReconnect && connectionParams && connectionParams.vmId) {
      attemptReconnect()
    }
  }

  /**
   * Connect to share portal VM console via WebSocket
   *
   * Two-step connection process:
   * 1. Get WebSocket token from central API using share session
   * 2. Connect to regional WebSocket with token
   *
   * Creates the Guacamole client with a WebSocket tunnel and establishes
   * the connection. Sets up keyboard and mouse input handlers.
   * Stores connection parameters for auto-reconnect.
   *
   * @param {string} shareId - Share UUID
   * @param {string} vmId - VM ID (prefixed format: vm-xxx)
   * @param {string} shareSessionToken - Share session token from sessionStorage
   * @throws {Error} If initializeClient not called first or token fetch fails
   * @example
   * // For external share users
   * const token = getShareSessionToken()
   * await connectShare(shareId, vmId, token)
   */
  async function connectShare(shareId, vmId, shareSessionToken) {
    if (!connectionParams || !connectionParams.displayElement) {
      throw new Error('Client not initialized. Call initializeClient() first.')
    }

    if (!shareSessionToken) {
      throw new Error('No share session token provided. User must be authenticated.')
    }

    try {
      // Store params for reconnection
      connectionParams.shareId = shareId
      connectionParams.vmId = vmId
      connectionParams.shareSessionToken = shareSessionToken
      connectionParams.connectionType = 'share'

      debug('[useConsole] Starting share connection for VM:', vmId)
      debug('[useConsole] Share ID:', shareId)
      connectionState.value = 'connecting'
      errorMessage.value = null

      // Step 1: Get regional WebSocket token from central API
      debug('[useConsole] Step 1: Fetching WebSocket token from central API...')
      const { ws_token, ws_url } = await getShareConsoleToken(shareId, vmId, shareSessionToken)
      debug('[useConsole] Token received, regional WebSocket URL:', ws_url)

      // Step 2: Build WebSocket URL with token
      const websocketUrl = `${ws_url}?token=${encodeURIComponent(ws_token)}`
      debug(
        '[useConsole] WebSocket URL:',
        websocketUrl.replace(/token=[^&]+/, 'token=***MASKED***')
      )

      // Step 3: Verify display element has valid dimensions before connecting
      const displayElement = connectionParams.displayElement
      const rect = displayElement.getBoundingClientRect()
      debug('[useConsole] Display element dimensions:', {
        width: rect.width,
        height: rect.height,
        offsetWidth: displayElement.offsetWidth,
        offsetHeight: displayElement.offsetHeight
      })

      if (rect.width === 0 || rect.height === 0) {
        const error = new Error(
          'Display element has zero dimensions. Ensure the container is visible and sized before connecting.'
        )
        console.error('[useConsole] Invalid display dimensions:', error)
        throw error
      }

      // Step 4: Create WebSocket tunnel and connect
      debug('[useConsole] Creating WebSocketTunnel...')
      tunnel = new window.Guacamole.WebSocketTunnel(websocketUrl)

      // Set up tunnel message interceptor and handlers (same as connect())
      const originalSendMessage = tunnel.sendMessage.bind(tunnel)
      tunnel.sendMessage = function (...args) {
        const opcode = args[0]
        debug('[useConsole] Tunnel sendMessage intercepted:', { opcode, args })

        if (opcode === 'size') {
          const MIN_DIMENSION = 100
          let width = parseInt(args[1], 10) || 1024
          let height = parseInt(args[2], 10) || 768

          if (width < MIN_DIMENSION || height < MIN_DIMENSION) {
            console.warn('[useConsole] Blocking invalid size instruction:', { width, height })
            width = Math.max(MIN_DIMENSION, width) || 1024
            height = Math.max(MIN_DIMENSION, height) || 768
            debug('[useConsole] Sending corrected size:', { width, height })
          }

          return originalSendMessage('size', width.toString(), height.toString())
        }

        return originalSendMessage.apply(this, args)
      }

      // Set tunnel state handler for debugging
      tunnel.onstatechange = state => {
        const stateNames = { 0: 'CONNECTING', 1: 'OPEN', 2: 'CLOSED' }
        debug('[useConsole] Tunnel state changed:', stateNames[state] || state)
      }

      tunnel.onerror = status => {
        console.error('[useConsole] Tunnel error:', status)
        handleError({
          message: `Tunnel error: ${status.message || 'Connection failed'}`,
          code: status.code
        })
      }

      // Create client and set up handlers (same as connect())
      // NOTE: The SERVER handles the full handshake - frontend just receives display data
      debug('[useConsole] Creating Guacamole client with tunnel...')
      client = new window.Guacamole.Client(tunnel)

      displayElement.appendChild(client.getDisplay().getElement())

      client.onstatechange = handleStateChange
      client.onerror = handleError
      client.onsync = timestamp => {
        debug('[useConsole] Sync received from guacd, timestamp:', timestamp)
        scaleDisplayToFit()
      }
      debug('[useConsole] Sync handler registered')

      if (onClipboardReceived) {
        client.onclipboard = stream => {
          const reader = new window.Guacamole.StringReader(stream)
          let clipboardText = ''
          reader.ontext = text => {
            clipboardText += text
          }
          reader.onend = () => {
            onClipboardReceived(clipboardText)
          }
        }
      }

      // Connect (credentials handled by backend)
      debug('[useConsole] Calling client.connect() (credentials handled by backend)...')
      client.connect('')
      debug('[useConsole] Client connect() called, WebSocket tunnel opening...')

      // Set up input handlers (keyboard and mouse) - same as connect()
      keyboard = new window.Guacamole.Keyboard(document)

      let modifierPressed = false
      const KEYSYM_CTRL_L = 0xffe3,
        KEYSYM_CTRL_R = 0xffe4
      const KEYSYM_META_L = 0xffe7,
        KEYSYM_META_R = 0xffe8
      const KEYSYM_V_LOWER = 0x76,
        KEYSYM_V_UPPER = 0x56

      const isModifierKey = keysym =>
        keysym === KEYSYM_CTRL_L ||
        keysym === KEYSYM_CTRL_R ||
        keysym === KEYSYM_META_L ||
        keysym === KEYSYM_META_R

      keyboard.onkeydown = keysym => {
        if (client && keyboardEnabled) {
          if (isModifierKey(keysym)) {
            modifierPressed = true
          }
          if (modifierPressed && (keysym === KEYSYM_V_LOWER || keysym === KEYSYM_V_UPPER)) {
            debug('[useConsole] Ctrl+V / Cmd+V detected, reading clipboard...')
            navigator.clipboard
              .readText()
              .then(text => {
                if (text) {
                  debug('[useConsole] Sending clipboard to VM:', text.substring(0, 50) + '...')
                  sendClipboard(text)
                }
              })
              .catch(err => console.warn('[useConsole] Could not read clipboard:', err.message))
            return false
          }
          client.sendKeyEvent(1, keysym)
          return false
        }
        return true
      }

      keyboard.onkeyup = keysym => {
        if (client && keyboardEnabled) {
          if (isModifierKey(keysym)) {
            modifierPressed = false
          }
          client.sendKeyEvent(0, keysym)
          return false
        }
        return true
      }

      const display = client.getDisplay()
      mouse = new window.Guacamole.Mouse(display.getElement())
      mouse.onmousedown =
        mouse.onmouseup =
        mouse.onmousemove =
          mouseState => {
            if (client) {
              client.sendMouseState(mouseState)
            }
          }

      const touch = new window.Guacamole.Mouse.Touchpad(display.getElement())
      touch.onmousedown =
        touch.onmouseup =
        touch.onmousemove =
          mouseState => {
            if (client) {
              client.sendMouseState(mouseState)
            }
          }

      const handlePaste = e => {
        if (client && connectionState.value === 'connected') {
          const text = e.clipboardData?.getData('text/plain')
          if (text) {
            debug('[useConsole] Paste detected, sending to VM clipboard')
            sendClipboard(text)
          }
        }
      }

      document.addEventListener('paste', handlePaste)
      connectionParams.pasteHandler = handlePaste
    } catch (err) {
      errorMessage.value = err.message || 'Failed to connect'
      connectionState.value = 'error'
      throw err
    }
  }

  /**
   * Send clipboard data to remote console
   *
   * Sends text to the remote VM's clipboard.
   *
   * @param {string} text - Text to send to clipboard
   * @example
   * sendClipboard('paste this text')
   */
  function sendClipboard(text) {
    if (client && connectionState.value === 'connected') {
      const stream = client.createClipboardStream('text/plain')
      const writer = new window.Guacamole.StringWriter(stream)
      writer.sendText(text)
      writer.sendEnd()
    }
  }

  /**
   * Enable or disable keyboard input capture
   *
   * When disabled, keyboard events are not sent to the remote console
   * and are allowed to propagate normally (e.g., for typing in dialogs).
   *
   * @param {boolean} enabled - Whether to enable keyboard capture
   * @example
   * setKeyboardEnabled(false) // Disable for modal dialogs
   * setKeyboardEnabled(true)  // Re-enable after dialog closes
   */
  function setKeyboardEnabled(enabled) {
    keyboardEnabled = enabled
    debug('[useConsole] Keyboard enabled:', enabled)
  }

  /**
   * Send Ctrl+Alt+Delete key combination to remote console
   *
   * Sends the special key combination commonly used for Windows login
   * and task manager. Keys are sent as press (1) then release (0).
   *
   * @example
   * sendCtrlAltDel()
   */
  function sendCtrlAltDel() {
    if (client && connectionState.value === 'connected') {
      // Press keys in sequence
      client.sendKeyEvent(1, KEY_SYMBOLS.CTRL)
      client.sendKeyEvent(1, KEY_SYMBOLS.ALT)
      client.sendKeyEvent(1, KEY_SYMBOLS.DELETE)

      // Release keys in reverse order
      client.sendKeyEvent(0, KEY_SYMBOLS.DELETE)
      client.sendKeyEvent(0, KEY_SYMBOLS.ALT)
      client.sendKeyEvent(0, KEY_SYMBOLS.CTRL)
    }
  }

  /**
   * Manually reconnect to console
   *
   * Resets reconnection attempts and initiates a new connection.
   * Useful when user wants to retry after max attempts exceeded.
   *
   * @example
   * reconnect()
   */
  function reconnect() {
    // Reset reconnect state
    reconnectAttempts.value = 0
    reconnectDelay.value = initialReconnectDelay
    errorMessage.value = null

    // Disconnect current connection if any
    if (connectionState.value !== 'disconnected') {
      disconnect(false)
    }

    // Attempt reconnection if we have the connection params
    if (connectionParams && connectionParams.vmId) {
      attemptReconnect()
    }
  }

  /**
   * Attempt automatic reconnection with exponential backoff
   *
   * Internal function that handles the auto-reconnect logic.
   * Implements exponential backoff strategy with max attempts limit.
   * Rebuilds WebSocket URL based on stored connection type and parameters.
   *
   * @private
   */
  function attemptReconnect() {
    // Check if max attempts exceeded
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      connectionState.value = 'error'
      errorMessage.value = `Failed to reconnect after ${maxReconnectAttempts} attempts`
      return
    }

    // Verify we have connection parameters
    if (!connectionParams || !connectionParams.vmId || !connectionParams.connectionType) {
      connectionState.value = 'error'
      errorMessage.value = 'Cannot reconnect: missing connection parameters'
      return
    }

    connectionState.value = 'reconnecting'
    reconnectAttempts.value += 1

    // Schedule reconnection attempt
    reconnectTimeoutId = setTimeout(async () => {
      try {
        // Attempt to connect with stored parameters based on connection type
        if (connectionParams.connectionType === 'authenticated') {
          await connect(connectionParams.vmId)
        } else if (connectionParams.connectionType === 'share') {
          await connectShare(
            connectionParams.shareId,
            connectionParams.vmId,
            connectionParams.shareSessionToken
          )
        } else {
          throw new Error(`Unknown connection type: ${connectionParams.connectionType}`)
        }
      } catch (err) {
        console.error('Reconnection attempt failed:', err)
        // Continue with next attempt
        scheduleNextReconnect()
      }
    }, reconnectDelay.value)

    // Calculate next delay with exponential backoff
    reconnectDelay.value = Math.min(reconnectDelay.value * 2, maxReconnectDelay)
  }

  /**
   * Schedule next reconnection attempt
   *
   * Called when a reconnection attempt fails.
   * Continues the exponential backoff sequence.
   *
   * @private
   */
  function scheduleNextReconnect() {
    if (reconnectAttempts.value < maxReconnectAttempts) {
      attemptReconnect()
    } else {
      connectionState.value = 'error'
      errorMessage.value = `Failed to reconnect after ${maxReconnectAttempts} attempts`
    }
  }

  /**
   * Handle Guacamole client state changes
   *
   * Internal handler for client connection state transitions.
   *
   * @param {number} state - Guacamole client state code
   */
  function handleStateChange(state) {
    const Guacamole = window.Guacamole

    debug('[useConsole] State change:', {
      stateCode: state,
      stateName: getStateName(state),
      currentConnectionState: connectionState.value
    })

    switch (state) {
      case Guacamole.Client.State.IDLE:
        debug('[useConsole] -> IDLE')
        connectionState.value = 'idle'
        break

      case Guacamole.Client.State.CONNECTING:
        debug('[useConsole] -> CONNECTING')
        connectionState.value = 'connecting'
        break

      case Guacamole.Client.State.WAITING:
        debug('[useConsole] -> WAITING')
        connectionState.value = 'connecting'

        // Send display size EARLY - guacd needs this before it can complete terminal setup
        // The WAITING state means the tunnel is connected but guacd is still initializing
        if (client && connectionParams && connectionParams.displayElement) {
          const displayElement = connectionParams.displayElement
          const rect = displayElement.getBoundingClientRect()

          // Get raw dimensions and log them
          const rawWidth = rect.width
          const rawHeight = rect.height
          debug('[useConsole] WAITING state - raw dimensions:', { rawWidth, rawHeight })

          // CRITICAL: Use strict minimum dimensions to avoid guacd assertion failures
          // guacd's guac_terminal_fit_to_range() will crash on dimensions < 1
          const MIN_WIDTH = 640
          const MIN_HEIGHT = 480
          const width = Math.max(MIN_WIDTH, Math.floor(rawWidth) || 1024)
          const height = Math.max(MIN_HEIGHT, Math.floor(rawHeight) || 768)

          debug('[useConsole] WAITING state - sending validated display size:', {
            width,
            height
          })

          // Send size instruction through tunnel
          // Client-to-server format: size,width,height (NO DPI - guacd doesn't expect it)
          // DPI should be passed during connection setup, not in the size instruction
          if (tunnel && typeof tunnel.sendMessage === 'function') {
            tunnel.sendMessage('size', width.toString(), height.toString())
            debug('[useConsole] Size instruction sent:', { width, height })
          } else {
            console.warn('[useConsole] tunnel.sendMessage not available')
          }
        }
        break

      case Guacamole.Client.State.CONNECTED:
        debug('[useConsole] -> CONNECTED (Success!)')
        connectionState.value = 'connected'
        errorMessage.value = null
        // Reset reconnect state on successful connection
        reconnectAttempts.value = 0
        reconnectDelay.value = initialReconnectDelay

        // CRITICAL: Send display size to guacd immediately after connecting
        // guacd needs to know the display dimensions to calculate terminal rows/cols
        // Without this, guacd may fail with assertion errors for SSH terminals
        if (client && connectionParams && connectionParams.displayElement) {
          const displayElement = connectionParams.displayElement
          const rect = displayElement.getBoundingClientRect()
          const width = Math.floor(rect.width) || 1024
          const height = Math.floor(rect.height) || 768

          debug('[useConsole] Sending display size to server:', { width, height })

          // Use the client's sendSize method if available
          if (typeof client.sendSize === 'function') {
            client.sendSize(width, height)
          } else {
            // Fallback: send size instruction directly through tunnel
            // Note: NO DPI - guacd size instruction only expects width,height
            debug('[useConsole] client.sendSize not available, trying tunnel')
            if (tunnel && typeof tunnel.sendMessage === 'function') {
              tunnel.sendMessage('size', width.toString(), height.toString())
            }
          }

          // Scale the display to fit the container
          scaleDisplayToFit()

          // Set up ResizeObserver to handle container resizing
          setupResizeObserver()
        }
        break

      case Guacamole.Client.State.DISCONNECTING:
        debug('[useConsole] -> DISCONNECTING')
        connectionState.value = 'disconnected'
        break

      case Guacamole.Client.State.DISCONNECTED: {
        debug('[useConsole] -> DISCONNECTED')
        // Only auto-reconnect if we were previously connected (not during initial connection)
        // Don't auto-reconnect if:
        // 1. We're already reconnecting (prevents infinite loops)
        // 2. We never successfully connected (prevents reconnecting on initial failure)
        const wasConnected = connectionState.value === 'connected'
        const isReconnecting = connectionState.value === 'reconnecting'

        if (wasConnected && !isReconnecting) {
          // Unexpected disconnect from an established connection - attempt reconnect
          debug(
            '[useConsole] Unexpected disconnect from established connection, will attempt reconnection'
          )
          disconnect(true)
        } else {
          // Initial connection failed, manual disconnect, or already reconnecting
          debug(
            '[useConsole] Connection terminated (wasConnected:',
            wasConnected,
            'isReconnecting:',
            isReconnecting,
            ')'
          )
          connectionState.value = 'disconnected'
        }
        break
      }

      default:
        debug('[useConsole] -> UNKNOWN STATE:', state)
        connectionState.value = 'idle'
    }
  }

  /**
   * Get human-readable state name for debugging
   * @param {number} state - Guacamole state code
   * @returns {string} State name
   */
  function getStateName(state) {
    const Guacamole = window.Guacamole
    const states = {
      [Guacamole.Client.State.IDLE]: 'IDLE',
      [Guacamole.Client.State.CONNECTING]: 'CONNECTING',
      [Guacamole.Client.State.WAITING]: 'WAITING',
      [Guacamole.Client.State.CONNECTED]: 'CONNECTED',
      [Guacamole.Client.State.DISCONNECTING]: 'DISCONNECTING',
      [Guacamole.Client.State.DISCONNECTED]: 'DISCONNECTED'
    }
    return states[state] || 'UNKNOWN'
  }

  /**
   * Handle Guacamole client errors
   *
   * Internal handler for connection and protocol errors.
   * Triggers auto-reconnect if within attempt limits.
   *
   * @param {Object} error - Error object from Guacamole
   * @param {string} error.message - Error message
   * @param {string|number} [error.code] - Error code
   */
  function handleError(error) {
    errorMessage.value = error.message || 'Connection error'

    console.error('Guacamole error:', error)

    // Attempt cleanup and reconnect
    disconnect(true)
  }

  // Auto-cleanup on component unmount
  onUnmounted(() => {
    // Clear any pending reconnect timeout
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }
    // Clean up ResizeObserver
    if (resizeObserver) {
      resizeObserver.disconnect()
      resizeObserver = null
    }
    // Disconnect without attempting reconnect
    disconnect(false)
  })

  return {
    /** @type {import('vue').Ref<ConnectionState>} Connection state */
    connectionState,

    /** @type {import('vue').Ref<string|null>} Error message if connection fails */
    errorMessage,

    /** @type {import('vue').Ref<number>} Current reconnection attempt count */
    reconnectAttempts,

    /** @type {import('vue').Ref<number>} Current reconnection delay in ms */
    reconnectDelay,

    /** Initialize client and attach to display element */
    initializeClient,

    /** Connect to authenticated VM console via WebSocket */
    connect,

    /** Connect to share portal VM console via WebSocket */
    connectShare,

    /** Disconnect and clean up resources */
    disconnect,

    /** Send text to remote clipboard */
    sendClipboard,

    /** Send Ctrl+Alt+Delete key combination */
    sendCtrlAltDel,

    /** Enable or disable keyboard capture */
    setKeyboardEnabled,

    /** Manually reconnect to console */
    reconnect,

    /** Manually rescale display to fit container */
    scaleDisplayToFit
  }
}
